// @ts-strict-ignore
import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MiddleEllipsisPipe } from '@app/_shared/pipes/middle-ellipsis.pipe';
import { SupportLinksService } from '@app/_shared/service';
import { NotificationsService } from '@app/_shared/service/http/notifications.service';
import { NotificationsPollService } from '@app/_shared/service/notifications-poll.service';
import { AccountingGroup, RouteGroup, RouteMappingService } from '@app/_shared/service/route-mapping.service';
import { ThemeService } from '@app/_shared/service/theme.service';
import * as BootstrapActions from '@app/main-store/bootstrap.actions';
import { getActiveStores, getDefaultStore } from '@app/main-store/bootstrap.selector';
import { selectShopifyUpgradeMessages } from '@app/main-store/integrations/integrations.selectors';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoModule, translateObjectSignal } from '@jsverse/transloco';
import { Store, select } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { OverlayBadge } from 'primeng/overlaybadge';
import { PanelMenuModule } from 'primeng/panelmenu';
import { Popover } from 'primeng/popover';
import { TagModule } from 'primeng/tag';
import { TieredMenu } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { map } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { SearchBarComponent } from '../search-bar/search-bar.component';

export interface ExtendedMenuItem extends MenuItem {
  showAddIcon?: boolean;
  showWarning?: boolean;
  showSettingsButton?: boolean;
  showBetaTag?: boolean;
  settingsCommand?: (event: any) => void;
  active?: () => boolean;
  badgeCount?: number;
  hiddenClass?: string;
  groupRouterLink?: string[];
  navigateWhenActive?: boolean;
}

@Component({
  selector: 'sb-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',

  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    ButtonModule,
    DividerModule,
    TieredMenu,
    PanelMenuModule,
    TooltipModule,
    SearchBarComponent,
    TranslocoModule,
    OverlayBadge,
    MiddleEllipsisPipe,
    TagModule,
    Popover,
    DatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarMenuComponent {
  private _authService = inject(AuthService);
  private supportLinkService = inject(SupportLinksService);
  private router = inject(Router);
  private rxStore = inject<Store<any>>(Store);
  private destroyRef = inject(DestroyRef);
  private routeMapping = inject(RouteMappingService);
  private notificationsPollService = inject(NotificationsPollService);
  private notificationsService = inject(NotificationsService);
  private themeService = inject(ThemeService);

  readonly activeRouteGroup = input<RouteGroup>('accounting');
  readonly activeAccountingGroup = input<AccountingGroup>('orders');
  readonly hideLogo = input(false);
  readonly actionCompleted = output<void>();

  readonly collapsed = input(false);
  readonly toggleCollapse = output<void>();

  readonly activeStoreId = model<string | number | null>(null);
  readonly storesWithErrors = toSignal(this.rxStore.select(selectShopifyUpgradeMessages));
  readonly stores = toSignal(this.rxStore.pipe(takeUntilDestroyed(this.destroyRef), select(getActiveStores)));
  readonly defaultStore = toSignal(
    this.rxStore.pipe(
      takeUntilDestroyed(this.destroyRef),
      select(getDefaultStore),
      map((store) => {
        if (store?.id) {
          this.activeStoreId.set(store.id);
        }
        return store;
      }),
    ),
  );
  readonly isDarkMode = this.themeService.isDarkModeSignal;

  private readonly expandedState = signal<Record<string, boolean | null>>({
    accounting: this.routeMapping.getRouteGroup(this.router.url) === 'accounting',
  });

  private accountMenu = viewChild<TieredMenu>('accountMenu');
  private businessMenu = viewChild<TieredMenu>('businessMenu');
  private notificationsPopover = viewChild<Popover>('notificationsPopover');

  readonly unseenAtOpen = signal<Set<string>>(new Set());
  private readonly alreadySeen = signal<Set<string>>(new Set());

  readonly filteredNotifications = computed(() => {
    const storeId = this.activeStoreId();
    const all = this.notificationsPollService.notifications();
    if (!storeId) return all;
    return all.filter((n) => n.attributes.store_id == null || n.attributes.store_id === +storeId);
  });

  readonly unreadNotificationsCount = computed(
    () =>
      this.filteredNotifications().filter((n) => n.attributes.read_at === null && !this.alreadySeen().has(String(n.id)))
        .length,
  );

  markAllNotificationsAsSeen(): void {
    const seen = this.alreadySeen();
    const newlyUnread = new Set(
      this.filteredNotifications()
        .filter((n) => n.attributes.read_at === null && !seen.has(String(n.id)))
        .map((n) => String(n.id)),
    );
    this.unseenAtOpen.set(newlyUnread);
    this.alreadySeen.set(new Set([...seen, ...newlyUnread]));
    if (newlyUnread.size > 0) {
      this.notificationsService.markAllAsRead().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  private storesWithErrorsSet = computed(() => {
    return new Set(this.storesWithErrors().map((s) => s.storeId));
  });

  private hasErrors = (storeId: number) => this.storesWithErrorsSet().has(storeId);

  private readonly currentRoute = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      startWith(this.router.url),
    ),
  );

  isNonDefaultStoresErrorFree = computed(
    () => this.storesWithErrors().filter((s) => s.storeId !== this.defaultStore()?.id).length === 0,
  );

  sidebarTranslationObject = translateObjectSignal('sidebar');
  accountingTranslation = translateObjectSignal('sidebar.accounting');
  toolboxTranslation = translateObjectSignal('sidebar.toolbox');
  crmTranslation = translateObjectSignal('sidebar.crm');

  sidePanelMenuItems = computed<ExtendedMenuItem[]>(() => {
    const currentUrl = this.currentRoute();
    const routeGroup = this.routeMapping.getRouteGroup(currentUrl);
    const accountingGroup = routeGroup === 'accounting' ? this.routeMapping.getAccountingGroup(currentUrl) : null;

    const accountingItems: ExtendedMenuItem[] = [
      {
        id: 'orders',
        label: this.accountingTranslation().sales,
        icon: 'basket-shopping',
        iconPrefix: 'fal',
        routerLink: ['/orders'],
        active: () => accountingGroup === 'orders',
      },
      {
        id: 'payments',
        label: this.accountingTranslation().payments,
        icon: 'money-bill-transfer',
        iconPrefix: 'fal',
        routerLink: ['/payments/gateway'],
        active: () => accountingGroup === 'payments',
      },
      {
        id: 'refundTransactions',
        label: this.accountingTranslation().refundTransactions,
        icon: 'money-bill-wave',
        iconPrefix: 'fal',
        routerLink: ['/refund-transactions/gateway'],
        active: () => accountingGroup === 'refundTransactions',
      },
      {
        id: 'corrections',
        label: this.accountingTranslation().corrections,
        icon: 'money-check-pen',
        iconPrefix: 'fal',
        routerLink: ['/corrections'],
        active: () => accountingGroup === 'corrections',
      },
      {
        id: 'bundles',
        label: this.accountingTranslation().bundles,
        icon: 'file-export',
        iconPrefix: 'fal',
        routerLink: ['/bundles'],
        active: () => accountingGroup === 'bundles',
      },
      {
        id: 'payouts',
        label: this.accountingTranslation().payouts,
        icon: 'piggy-bank',
        iconPrefix: 'fal',
        routerLink: ['/payouts'],
        active: () => accountingGroup === 'payouts',
      },
    ];

    return [
      {
        id: 'home',
        label: this.sidebarTranslationObject().home,
        icon: 'house',
        iconPrefix: 'fal',
        active: () => routeGroup === 'home',
        groupRouterLink: ['/'],
      },
      {
        id: 'accounting',
        label: this.accountingTranslation().title,
        icon: 'calculator',
        iconPrefix: 'fal',
        active: () => routeGroup === 'accounting',
        expanded: this.expandedState()['accounting'],
        items: accountingItems,
        command: () => this.expandedState.update((s) => ({ ...s, accounting: !s.accounting })),
      },
      {
        id: 'bank',
        label: this.accountingTranslation().bank,
        icon: 'light-building-columns-circle-dollar',
        iconPrefix: 'fak',
        active: () => routeGroup === 'bank',
        groupRouterLink: ['/bank'],
        navigateWhenActive: true,
      },
      {
        id: 'invoicing',
        label: this.accountingTranslation().invoicing,
        icon: 'file-invoice',
        iconPrefix: 'fal',
        active: () => routeGroup === 'invoicing',
        groupRouterLink: ['/mailed-documents'],
      },
      {
        id: 'crm',
        label: this.crmTranslation().title,
        icon: 'users',
        iconPrefix: 'fal',
        active: () => routeGroup === 'crm',
        groupRouterLink: ['/customers'],
      },
      {
        id: 'tools',
        label: this.toolboxTranslation().title,
        icon: 'screwdriver-wrench',
        iconPrefix: 'fal',
        active: () => routeGroup === 'tools',
        groupRouterLink: ['/tools'],
        navigateWhenActive: true,
      },
    ];
  });

  accountMenuItems = computed<MenuItem[]>(() => [
    {
      label: this.sidebarTranslationObject().companyAndAccount,
      routerLink: ['/settings/company'],
      command: () => this.notifyActionCompleted(),
    },
    { separator: true },
    {
      label: this.sidebarTranslationObject().terms,
      routerLink: ['/settings/company'],
      queryParams: { tab: 'terms' },
      command: () => this.notifyActionCompleted(),
    },
    { separator: true } as MenuItem,
    { label: this.sidebarTranslationObject().logout, command: () => this.logout() },
  ]);

  businessMenuItems = computed<ExtendedMenuItem[]>(() => {
    const items: ExtendedMenuItem[] = [
      {
        label: this.sidebarTranslationObject().allStores,
        command: () => this.onStoreSelectAction(null),
        active: () => false,
      },
    ];

    (this.stores() ?? []).forEach((element) => {
      items.push({
        label: element.attributes.name,
        command: () => this.onStoreSelectAction(+element.id),
        active: () => this.defaultStore()?.id === +element.id,
        showSettingsButton: true,
        showWarning: this.hasErrors(+element.id),
        settingsCommand: (event) => {
          this.onStoreSelectAction(+element.id, '/settings/store/' + +element.id);
        },
      });
    });

    if (items.length > 1) {
      items.push({ separator: true });
    }

    items.push({
      label: this.sidebarTranslationObject().addStore,
      routerLink: ['/stores/sales-platform'],
      showAddIcon: true,
    });

    return items;
  });

  toSidebarFooterMenuItem = (item: any) => item as ExtendedMenuItem;

  getRouterLink(item: ExtendedMenuItem | null | undefined): MenuItem['routerLink'] | null {
    if (!item) return null;

    const link = item.groupRouterLink ?? item.routerLink;
    if (link === undefined) return null;

    const shouldPreventNavigation = item.active?.() && !item.navigateWhenActive;
    if (shouldPreventNavigation) return null;

    return link;
  }

  sidebarFooterMenubarItems = computed<ExtendedMenuItem[]>(() => [
    {
      id: 'company-settings',
      icon: 'building',
      iconPrefix: 'fal',
      tooltip: this.sidebarTranslationObject().companySettings,
      tooltipPosition: 'top',
      active: () => this.accountMenu().visible || this.activeRouteGroup() === 'company-settings',
      command: (event) => this.toggleAccountMenu(event.originalEvent),
    },
    {
      id: 'settings',
      icon: 'gear',
      iconPrefix: 'fal',
      tooltip: this.sidebarTranslationObject().settings,
      disabled: !this.activeStoreId(),
      active: () => this.activeRouteGroup() === 'store-settings',
      routerLink: ['/settings/store/' + this.activeStoreId()],
      showWarning: this.defaultStore()?.id ? this.hasErrors(+this.defaultStore()?.id) : false,
    },
    {
      id: 'support',
      iconPrefix: 'fal',
      icon: 'circle-question',
      tooltip: this.sidebarTranslationObject().help,
      command: () => this.navigateToSupportPage(),
    },
    {
      id: 'notifications',
      icon: 'bell',
      iconPrefix: 'fal',
      tooltip: this.sidebarTranslationObject().notifications,
      tooltipPosition: 'top',
      active: () => this.notificationsPopover()?.overlayVisible ?? false,
      badgeCount: this.unreadNotificationsCount(),
      command: (event) => this.toggleNotificationsPopover(event.originalEvent),
    },
  ]);

  navigateToSupportPage(): void {
    const supportLink = this.supportLinkService.getList().mainLayout.helpCenter;
    window.open(supportLink, '_blank', 'noopener,noreferrer');
  }

  toggleNotificationsPopover(event: Event): void {
    this.notificationsPopover()?.toggle(event);
  }

  toggleAccountMenu(event: any): void {
    const actualEvent = event instanceof Event ? event : event?.originalEvent || event;
    if (actualEvent) {
      this.accountMenu()?.toggle(actualEvent);
    }
  }

  toggleBusinessMenu(event: Event): void {
    this.businessMenu()?.toggle(event);
  }

  notifyActionCompleted(): void {
    this.actionCompleted.emit();
  }

  navigateToSettingsPage(): void {
    const storeId = this.activeStoreId();
    if (storeId) {
      this.router.navigate(['/settings/store/' + storeId]);
    }
  }

  handleCollapsedItemClick(event: Event, item: ExtendedMenuItem, menu?: TieredMenu) {
    if (item.items?.length && menu) {
      menu.toggle(event);
    } else if (item.command) {
      item.command({ originalEvent: event });
    } else {
      this.notifyActionCompleted();
    }
  }

  logout() {
    this.notifyActionCompleted();
    this._authService.logout({
      logoutParams: {
        returnTo: `https://storebuddy.dk`,
      },
    });
  }

  onStoreSelectAction(storeId: number | null, route: string = '/') {
    this.notifyActionCompleted();
    this.rxStore.dispatch(BootstrapActions.setDefaultStoreById({ storeId: storeId ?? null }));
    this.activeStoreId.set(storeId);

    this.router.navigate([route]);
  }

  getStoreName(storeId: number | null | undefined): string | null {
    if (storeId == null) return null;
    return this.stores()?.find((s) => +s.id === storeId)?.attributes.name ?? null;
  }

  isProductionEnvironment(): boolean {
    return environment.environment === 'production';
  }
}
